// ============================================================================
// CHANGO V5.0 — AuthContext + AuthProvider
// ============================================================================
// Task: T-D1-018 — Plan §3.1 Auth / §3.7 Workspace
//
// Shape:
//   const {
//     user,              // { id, email, role, workspace_id } | null
//     workspace,         // backend /api/me 'workspace' slice | null
//     isAuthenticated,   // boolean
//     isLoading,         // boolean (initial hydrate)
//     error,             // HttpError | null (last network error)
//     login(email, pw),
//     loginTwoFa(challenge, code),
//     register(payload),
//     logout(),
//     refresh(),         // manual refresh of /api/me
//     logoutAll()        // kick all devices
//   } = useAuth();
//
// Hydration strategy:
//   On mount we call GET /api/me. If the server returns 200 → we're logged in,
//   populate state. If 401 TOKEN_EXPIRED, the HTTP client auto-refreshes and
//   retries once. If that also 401s, we flag not authenticated and move on.
// ============================================================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { api, HttpError } from '../api/client.js';
import { identify, resetIdentity, trackEvent, captureError } from '../lib/telemetry.js';
import { hasAnyDirtyForm } from '../hooks/useDirtyForm.js';

// §9.5.1 客户端自动刷新轮询周期
const LAYERS_WATCHER_POLL_MS = 60_000;       // 60 秒
const LAYERS_WATCHER_AUTO_RELOAD_DELAY = 3_000; // 3 秒后自动刷新 (无 dirty form 时)

const AuthContext = createContext(null);

// After the 4-SPA split (Task #53 / #55), the route where unauthenticated
// users should land differs per entry:
//   • app bundle  → /login
//   • ops bundle  → /ops/login
// The entry wires its `loginPath` through <AuthProvider loginPath=…>, and
// RequireAuth reads it via the context rather than hard-coding '/login'.
//
// This default (`/login`) preserves pre-split behaviour for any caller that
// didn't pass the prop, so existing tests and the legacy monolith main.jsx
// continue to work unchanged.
const DEFAULT_LOGIN_PATH = '/login';

export function AuthProvider({ children, loginPath = DEFAULT_LOGIN_PATH }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hydrate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await api.get('/api/me');
      if (me?.user) {
        const role = me.role || (me.workspace ? null : 'admin');
        const workspaceId = me.workspace?.id || null;
        setUser({
          id: me.user.id,
          email: me.user.email,
          contact_name: me.user.contact_name || '',
          display_name: me.user.display_name || me.user.contact_name || '',
          title: me.user.title || '',
          phone: me.user.phone || '',
          phone_verified_at: me.user.phone_verified_at || null,
          email_verified: Boolean(me.user.email_verified_at),
          role,
          // §ST.10 Plan B: is_owner 在 /api/me 顶层，setUser 必须合并到 user 对象（Round 12 cutover bug fix）
          is_owner: Boolean(me.is_owner),
          subject_type: me.subject_type || null,
          workspace_id: workspaceId,
          two_fa_enabled: Boolean(me.user.two_fa_enabled)
        });
        setWorkspace(me.workspace || null);
        // D5.4 — bind telemetry identity with role/workspace only. No PII.
        try { identify(me.user.id, { role: role || 'unknown', workspaceId: workspaceId || 'none' }); } catch {}
      } else {
        setUser(null);
        setWorkspace(null);
      }
    } catch (err) {
      // 401 after retry → genuinely not authenticated; stay silent.
      if (!(err instanceof HttpError) || err.status !== 401) {
        setError(err);
        try { captureError(err, { kind: 'hydrate' }); } catch {}
      }
      setUser(null);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // ──────────────────────────────────────────────────────────────────────
  // §9.5.1 客户端自动刷新机制: 60 秒轮询 /api/me, 比对 layers_revision,
  // 变化即弹 toast + 自动 reload (无 dirty form 时) 或手动 reload。
  // 只在已登录 + 有 workspace 时启动。
  // ──────────────────────────────────────────────────────────────────────
  const lastRevisionRef = useRef(null);
  const reloadTimerRef = useRef(null);
  const [layersToast, setLayersToast] = useState(null);   // {revision, sku_code} | null

  useEffect(() => {
    if (!user || !workspace) {
      lastRevisionRef.current = null;
      return undefined;
    }
    // 第一次 mount 时记录基线
    if (lastRevisionRef.current == null) {
      lastRevisionRef.current = workspace.layers_revision ?? 1;
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const me = await api.get('/api/me');
        if (cancelled) return;
        const newRev = me?.workspace?.layers_revision;
        if (newRev != null && lastRevisionRef.current != null
            && newRev !== lastRevisionRef.current) {
          // 检测到变化
          lastRevisionRef.current = newRev;
          setLayersToast({
            revision: newRev,
            sku_code: me.workspace.sku_code,
            enabled_layers: me.workspace.enabled_layers || []
          });
          // 没 dirty form 就 3 秒后自动刷新
          if (!hasAnyDirtyForm()) {
            reloadTimerRef.current = setTimeout(() => {
              try { window.location.reload(); } catch {}
            }, LAYERS_WATCHER_AUTO_RELOAD_DELAY);
          }
          // 有 dirty form 时, toast 自带"立即刷新"按钮, 不自动 reload
        }
      } catch (err) {
        // 静默吞掉错误 (网络抖动 / 401), 下个周期再试
      }
    };
    const interval = setInterval(tick, LAYERS_WATCHER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [user?.id, workspace?.id]);

  const dismissLayersToast = useCallback(() => {
    setLayersToast(null);
    if (reloadTimerRef.current) {
      clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = null;
    }
  }, []);

  const reloadNow = useCallback(() => {
    try { window.location.reload(); } catch {}
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      // Server may return a 2FA challenge instead of a session cookie.
      if (res?.two_factor_required) {
        return { twoFactor: res };
      }
      await hydrate();
      try { trackEvent('auth.login_ok', { two_factor: false }); } catch {}
      return { ok: true };
    } catch (err) {
      setError(err);
      try {
        trackEvent('auth.login_failed', {
          code: err instanceof HttpError ? err.code : 'UNKNOWN',
          status: err instanceof HttpError ? err.status : 0
        });
      } catch {}
      throw err;
    }
  }, [hydrate]);

  const loginTwoFa = useCallback(async (challenge, code) => {
    setError(null);
    try {
      await api.post('/api/auth/2fa/login', { challenge, code });
      await hydrate();
      try { trackEvent('auth.login_ok', { two_factor: true }); } catch {}
      return { ok: true };
    } catch (err) {
      setError(err);
      try {
        trackEvent('auth.login_failed', {
          code: err instanceof HttpError ? err.code : 'UNKNOWN',
          two_factor: true
        });
      } catch {}
      throw err;
    }
  }, [hydrate]);

  const register = useCallback(async (payload) => {
    setError(null);
    try {
      const res = await api.post('/api/auth/register', payload);
      // Server may or may not auto-login; hydrate to reflect reality.
      await hydrate();
      try { trackEvent('auth.register_completed', { auto_login: !!res?.auto_login }); } catch {}
      return res;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [hydrate]);

  const logout = useCallback(async () => {
    setError(null);
    try {
      await api.post('/api/auth/logout', {});
    } catch (err) {
      // Logout is best-effort; clear client state regardless.
      setError(err);
    } finally {
      setUser(null);
      setWorkspace(null);
      try { resetIdentity(); } catch {}
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await api.post('/api/me/logout-all', {});
    } finally {
      setUser(null);
      setWorkspace(null);
      try { resetIdentity(); } catch {}
    }
  }, []);

  const value = useMemo(() => ({
    user,
    workspace,
    isAuthenticated: Boolean(user),
    isLoading,
    error,
    clearError: () => setError(null),
    login,
    loginTwoFa,
    register,
    logout,
    logoutAll,
    refresh: hydrate,
    // §9.5.1 客户端自动刷新通知 (套餐被 ops 改动时弹 toast)
    layersToast,         // {revision, sku_code, enabled_layers} | null
    dismissLayersToast,
    reloadNow,
    // Exposed so RequireAuth (and any future auth-gated redirect) can send
    // the user to the correct login route for the current entry bundle.
    loginPath
  }), [user, workspace, isLoading, error, login, loginTwoFa, register, logout, logoutAll, hydrate, loginPath, layersToast, dismissLayersToast, reloadNow]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook accessor. Throws if used outside <AuthProvider> — surfacing bugs
 * loudly is better than silently giving undefined behaviour.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
